import React, { useState, useMemo } from 'react';
import { Box, Stack, Typography, Skeleton, Snackbar, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  useStoreManagement,
  Store,
  filterStores,
  getSelectedStores,
} from '../../context/StoreManagementContext';
import { useStoresQuery } from '../../hooks/queries';
import { StoreListView } from './StoreListView';
import { StoreGridView } from './StoreGridView';
import { UnifiedActionBar } from './UnifiedActionBar';
import { BulkOperationsDialog } from './BulkOperationsDialog';
import { StoreHoursTemplateDialog } from './StoreHoursTemplateDialog';
import { DEFAULT_STORE_FUNCTION } from './storeDetailTabs';

type SortOption = 'name' | 'number' | 'status' | 'modified';

export const HybridStoreManagement: React.FC = () => {
  // Get stores from TanStack Query
  const { data: stores = [], isLoading } = useStoresQuery();

  // Get UI state from context
  const {
    selectedStoreIds,
    selectionCount,
    filters,
    clearSelection,
    successMessage,
    showSuccess,
    clearSuccess,
  } = useStoreManagement();

  // Compute filtered and selected stores using helpers
  const filteredStores = useMemo(() => filterStores(stores, filters), [stores, filters]);
  const selectedStores = useMemo(
    () => getSelectedStores(stores, selectedStoreIds),
    [stores, selectedStoreIds]
  );

  const navigate = useNavigate();
  const [error] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isApplyingBulkOperation, setIsApplyingBulkOperation] = useState(false);
  const [bulkOperationDialogType, setBulkOperationDialogType] = useState<
    'hours' | 'channels' | 'items' | 'special-hours' | null
  >(null);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [templateDialogMode, setTemplateDialogMode] = useState<'save' | 'apply' | 'manage' | null>(
    null
  );

  // Sort stores based on selected sort option
  const sortedStores = useMemo(() => {
    const storesCopy = [...filteredStores];

    switch (sortBy) {
      case 'name':
        return storesCopy.sort((a, b) => (a.storeName || '').localeCompare(b.storeName || ''));
      case 'number':
        return storesCopy.sort((a, b) => a.storeNumber.localeCompare(b.storeNumber));
      case 'status':
        // Sort by online orders status (online first), then by name
        return storesCopy.sort((a, b) => {
          if (a.onlineOrders === b.onlineOrders) {
            return (a.storeName || '').localeCompare(b.storeName || '');
          }
          return a.onlineOrders ? -1 : 1;
        });
      case 'modified':
        // Sort by last modified (most recent first)
        return storesCopy.sort((a, b) => {
          const aTime = a.lastModified?.timestamp
            ? new Date(a.lastModified.timestamp).getTime()
            : 0;
          const bTime = b.lastModified?.timestamp
            ? new Date(b.lastModified.timestamp).getTime()
            : 0;
          return bTime - aTime;
        });
      default:
        return storesCopy;
    }
  }, [filteredStores, sortBy]);

  const handleBulkOperationSuccess = (message: string) => {
    showSuccess(message); // Use context - survives remounts
    clearSelection(); // Clear selected stores after successful bulk operation
  };

  const handleStoreClick = (store: Store) => {
    // Navigate to store detail page, landing on the first tab.
    navigate(`/stores/${store.storeNumber}/${DEFAULT_STORE_FUNCTION}`);
  };

  const handleBulkEdit = (action: string, data?: any) => {
    // Open the bulk operations dialog with the specific operation type
    if (action === 'bulk-edit-hours') {
      setBulkOperationDialogType('hours');
    } else if (action === 'bulk-edit-special-hours') {
      setBulkOperationDialogType('special-hours');
    } else if (action === 'bulk-edit-channels') {
      setBulkOperationDialogType('channels');
    } else if (action === 'bulk-edit-items') {
      setBulkOperationDialogType('items');
    } else if (action === 'save-hours-template') {
      setTemplateDialogMode('save');
    } else if (action === 'apply-holiday-hours' || action === 'apply-weekend-hours') {
      setTemplateDialogMode('apply');
    } else if (action === 'download-current-view') {
      // TODO: Implement CSV export
    } else if (action === 'snoozed-items-report') {
      // TODO: Implement snoozed items report
    } else if (action === 'view-recent-changes') {
      navigate('/activity');
    } else {
      setHasUnsavedChanges(true);
    }
  };

  const handleApplyTemplate = (templateId: string) => {
    setIsApplyingBulkOperation(true);
    // TODO: Implement apply template logic via API
    setTimeout(() => {
      setIsApplyingBulkOperation(false);
      setTemplateDialogMode(null);
    }, 2000);
  };

  const handleBulkOperationsApply = async (operation: string, data: any) => {
    setIsApplyingBulkOperation(true);
    try {
      // TODO: Implement actual API calls based on operation type
      // For now, simulate the operation
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Close the dialog after successful operation
      setBulkOperationDialogType(null);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to apply bulk operation:', error);
    } finally {
      setIsApplyingBulkOperation(false);
    }
  };

  const renderFunctionContent = () => {
    if (viewMode === 'grid') {
      return (
        <StoreGridView
          stores={sortedStores}
          onStoreClick={handleStoreClick}
          isLoading={isLoading}
        />
      );
    }

    return (
      <StoreListView
        stores={sortedStores}
        onStoreClick={handleStoreClick}
        isLoading={isLoading}
        viewMode={viewMode}
      />
    );
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {/* Action Bar */}
        <UnifiedActionBar
          onBulkEdit={handleBulkEdit}
          hasUnsavedChanges={hasUnsavedChanges}
          isApplying={isApplyingBulkOperation}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          sortBy={sortBy}
          onSortChange={setSortBy}
          filteredStores={filteredStores}
          selectedStores={selectedStores}
        />

        {/* Main Content Area */}
        <Box
          sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: 'background.default', mt: -0.1 }}
        >
          {isLoading ? (
            <Box sx={{ p: { xs: 1, sm: 3 } }}>
              <Stack spacing={2}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={80} />
                ))}
              </Stack>
            </Box>
          ) : (
            renderFunctionContent()
          )}
        </Box>

        {/* Bulk Operations Dialog */}
        <BulkOperationsDialog
          open={bulkOperationDialogType !== null}
          onClose={() => setBulkOperationDialogType(null)}
          selectedStoresCount={selectionCount}
          selectedStoreNumbers={Array.from(selectedStoreIds)}
          onApply={handleBulkOperationsApply}
          operationType={bulkOperationDialogType || 'hours'}
          onSuccess={handleBulkOperationSuccess}
        />

        {/* Store Hours Template Dialog */}
        <StoreHoursTemplateDialog
          open={templateDialogMode !== null}
          onClose={() => setTemplateDialogMode(null)}
          mode={templateDialogMode || 'apply'}
          onApplyTemplate={handleApplyTemplate}
        />
      </Box>

      {/* Success Notification */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={clearSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={clearSuccess} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};
